import { CommonModule } from '@angular/common';
import { AfterViewChecked, Component, ElementRef, OnDestroy, OnInit, effect, signal, computed, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { BusinessConfigService } from '../../services/business-config.service';
import { DialogService } from '../../services/dialog.service';
import { NotificationService } from '../../services/notification.service';
import { SalaService } from '../../services/sala.service';
import { MiSitioUnsavedChangesService } from '../../services/mi-sitio-unsaved-changes.service';
import { friendlyError } from '../../utils/friendly-error';
import { BusinessSiteInfo, BusinessPrivacyInfo, BusinessSocialLink, SocialNetworkOption, VisibleSections } from '../../models/sala.model';
import { PlatformSocialLinksAdminComponent, normalizeSocialUrl, validateSocialLinks } from '../ui/platform-social-links-admin.component';
import { UiStateComponent } from '../ui/ui-state.component';
import { PhotoTipsComponent } from '../ui/photo-tips.component';
import { environment } from '../../../environments/environment';
import * as QRCode from 'qrcode';
import { InvitationEditorComponent } from './invitation-editor.component';

type MiSitioSection = 'general' | 'portada' | 'colores' | 'informacion' | 'secciones' | 'redes' | 'vista-enlace' | 'privado';
type PreviewDevice = 'desktop' | 'mobile';

const DEFAULT_PRIMARY_COLOR = '#2563eb';
const DEFAULT_SECONDARY_COLOR = '#6c63ff';
const DEFAULT_BACKGROUND_COLOR = '#f6f5f0';
const DEFAULT_NAVBAR_COLOR = '#ffffff';
const DEFAULT_HIGHLIGHT_TEXT_COLOR = '#1f2937';
const DEFAULT_VISIBLE_SECTIONS: VisibleSections = { spaces: true, featured: true, about: true, contact: true };

/** Ancho/alto "reales" a los que se renderiza el sitio dentro del mockup de
 * laptop -- el iframe SIEMPRE carga a este ancho de escritorio real (nunca al
 * ancho angosto del panel de vista previa), y luego se reduce visualmente con
 * `transform: scale()` para que la miniatura muestre fielmente el layout de
 * escritorio en vez del layout móvil que el sitio mostraría si el iframe
 * fuera literalmente angosto. */
const LAPTOP_IFRAME_WIDTH = 1360;
// Alto de una ventana de laptop REAL (no "todo el contenido" -- ver nota
// larga más abajo, junto al mockup, sobre por qué intentar eso rompía el
// scroll). El iframe scrollea su propio documento de forma nativa, igual
// que una laptop de verdad: el dueño ve lo que entra en pantalla y puede
// scrollear con la rueda del mouse para ver el resto, tal como en un
// navegador real.
const LAPTOP_IFRAME_HEIGHT = 900;
/** Mismo criterio que el laptop, pero a escala de un viewport móvil real. */
const PHONE_IFRAME_HEIGHT = 760;
/** Ancho máximo del "cascarón" de laptop en pantalla (ver ResizeObserver más abajo). */
const LAPTOP_SHELL_MAX_WIDTH = 560;
/** Padding interno del bisel de pantalla (ver .ms-mock-screen) que hay que
 * descontar al calcular cuánto espacio real queda para el iframe escalado. */
const LAPTOP_SHELL_CHROME = 28;

/** Mensaje que viaja por postMessage hacia el iframe de vista previa cada vez
 * que cambia cualquier campo del borrador -- ver GalleryComponent (front
 * público) para el lado que lo recibe y aplica sin depender de "Guardar
 * borrador". El shape debe coincidir con MiSitioDraftPayload en gallery.ts. */
const DRAFT_PREVIEW_MESSAGE_TYPE = 'tusala:mi-sitio-draft';

interface MiSitioDraftPayload {
  businessName: string;
  bannerUrl: string | null;
  primaryColor: string;
  secondaryColor: string;
  backgroundColor: string;
  navbarColor: string;
  highlightTextColor: string;
  heroTitle: string;
  heroSubtitle: string;
  ctaText: string;
  shortDescription: string;
  aboutText: string;
  address: string;
  /** Redes visibles con enlace, ya con ícono y colores (mismo formato que
   * business.socialLinks de la API pública). */
  socialLinks: { id: number; network: string; label: string; url: string; icon: string; color: string; on_color: string }[];
  visibleSections: VisibleSections;
}

/** Forma comparable de una lista de redes (para detectar cambios sin guardar). */
const serializeSocialLinks = (links: BusinessSocialLink[]): string =>
  JSON.stringify(links.map(({ network, label, url, is_active }) => ({ network, label: (label || '').trim(), url: (url || '').trim(), is_active: !!is_active })));

@Component({
  selector: 'app-mi-sitio',
  standalone: true,
  imports: [CommonModule, UiStateComponent, InvitationEditorComponent, PlatformSocialLinksAdminComponent, PhotoTipsComponent],
  template: `
    <div class="mi-sitio-page">
      <div class="ms-header" *ngIf="mySite() as site">
        <div class="ms-status">
          <span class="ms-status-badge" [class.is-published]="site.status === 'published'">
            <i class="bi bi-circle-fill" aria-hidden="true"></i>
            {{ site.status === 'published' ? 'Publicado' : 'Borrador' }}
          </span>
          <small *ngIf="lastUpdatedLabel()">Última actualización: {{ lastUpdatedLabel() }}</small>
        </div>
        <div class="ms-header-actions">
          <button type="button" class="ms-btn ms-btn-ghost" [disabled]="!mySiteUrl() || isCopyingSiteUrl()" (click)="copyMySiteUrl()" title="Copiar el enlace de tu sitio">
            <i class="bi bi-clipboard" aria-hidden="true"></i> Copiar enlace
          </button>
          <button type="button" class="ms-btn ms-btn-ghost" [disabled]="!mySiteUrl() || isSharingSiteUrl()" (click)="shareMySiteUrl()" title="Compartir tu sitio">
            <i class="bi bi-share" aria-hidden="true"></i> Compartir
          </button>
          <button type="button" class="ms-btn ms-btn-ghost" [disabled]="!mySiteUrl()" (click)="verSitio()">
            <i class="bi bi-box-arrow-up-right" aria-hidden="true"></i> Ver sitio
          </button>
          <button type="button" class="ms-btn ms-btn-ghost" [disabled]="canCustomizeSite() && (isSavingDraft() || isPublishing())" (click)="canCustomizeSite() ? guardarBorrador(true) : showUpgradePrompt()">
            <i class="bi bi-save" aria-hidden="true"></i> {{ isSavingDraft() ? 'Guardando...' : 'Guardar borrador' }}
          </button>
          <button type="button" class="ms-btn ms-btn-primary" [disabled]="canCustomizeSite() && (isSavingDraft() || isPublishing())" (click)="canCustomizeSite() ? publicarCambios() : showUpgradePrompt()">
            <i class="bi bi-cloud-arrow-up" aria-hidden="true"></i> {{ isPublishing() ? 'Publicando...' : 'Publicar cambios' }}
          </button>
        </div>
      </div>

      <app-ui-state *ngIf="mySiteLoading()" tone="loading" title="Cargando tu sitio" message="Un momento..."></app-ui-state>
      <app-ui-state *ngIf="!mySiteLoading() && mySiteError()" tone="error" title="No pudimos cargar tu sitio" [message]="mySiteError()" actionLabel="Reintentar" (action)="loadMySite()"></app-ui-state>

      <div class="ms-body" *ngIf="!mySiteLoading() && !mySiteError() && mySite() as site">
        <div class="ms-premium-lock" *ngIf="!canCustomizeSite()">
          <i class="bi bi-stars" aria-hidden="true"></i>
          <div>
            <strong>Personalizar tu sitio no está incluido en tu plan actual.</strong>
            <p>Podés ver tu enlace público, pero para editar el contenido y la apariencia necesitás escalar de plan.</p>
          </div>
          <button type="button" (click)="goToSubscription()">Actualizar plan</button>
        </div>

        <nav class="ms-subnav">
          <button type="button" *ngFor="let item of sections" class="ms-subnav-item" [class.active]="activeSection()===item.id" (click)="selectSection(item.id)">
            <i [class]="item.icon" aria-hidden="true"></i> {{ item.label }}
          </button>
        </nav>

        <div class="ms-columns">
          <div class="ms-form" [class.is-locked]="!canCustomizeSite()">
            <button type="button" class="ms-locked-overlay" *ngIf="!canCustomizeSite()" (click)="showUpgradePrompt()" aria-label="Actualiza tu plan para personalizar tu sitio"></button>
            <!-- General -->
            <ng-container *ngIf="activeSection()==='general'">
              <h2>Información general</h2>
              <p class="ms-section-hint">Los cambios de esta sección se guardan solos; el resto de "Mi sitio" se guarda con los botones de arriba.</p>

              <label class="ms-field">
                <span class="ms-field-label">Nombre de tu negocio</span>
                <input type="text" [value]="businessNameDraft()" (input)="businessNameDraft.set($any($event.target).value)" (blur)="guardarNombreSiCambio()" maxlength="100" placeholder="Nombre de tu negocio">
              </label>

              <label class="ms-field">
                <span class="ms-field-label">Descripción corta<small>{{ shortDescriptionDraft().length }}/200</small></span>
                <textarea rows="2" [value]="shortDescriptionDraft()" (input)="shortDescriptionDraft.set($any($event.target).value)" maxlength="200" [disabled]="!canCustomizeSite()" placeholder="Salas de reunión, oficinas privadas y espacios de coworking para impulsar tus ideas."></textarea>
              </label>

              <div class="ms-field">
                <span class="ms-field-label">Logo del negocio <app-photo-tips kind="logo"></app-photo-tips></span>
                <div class="ms-logo-row">
                  <div class="ms-logo-preview">
                    <img *ngIf="logoPreview() || currentBusiness()?.logo" [src]="logoPreview() || getLogoUrl(currentBusiness()?.logo)" alt="Logo del negocio">
                    <div *ngIf="!logoPreview() && !currentBusiness()?.logo" class="ms-logo-empty"><i class="bi bi-image" aria-hidden="true"></i></div>
                  </div>
                  <div class="ms-logo-actions">
                    <input type="file" id="miSitioLogoInput" accept="image/png,image/jpeg,image/jpg,image/webp,image/gif" (change)="onLogoSelected($event)" class="ms-file-input">
                    <label for="miSitioLogoInput" class="ms-btn ms-btn-small ms-btn-ghost"><i class="bi bi-cloud-arrow-up" aria-hidden="true"></i> {{ isUploadingLogo() ? 'Subiendo...' : 'Cambiar imagen' }}</label>
                  </div>
                </div>
                <small class="ms-hint">Formatos: PNG, JPG, WEBP, GIF. Máx. 5MB. Se guarda al seleccionarla.</small>
              </div>

              <label class="ms-field">
                <span class="ms-field-label">URL de tu sitio</span>
                <div class="ms-slug-row">
                  <span class="ms-slug-prefix">{{ clientAppBaseUrl }}/</span>
                  <input type="text" [value]="slugDraft()" (input)="slugDraft.set($any($event.target).value)" maxlength="60" [disabled]="!canCustomizeSlug()" placeholder="nombre-de-tu-negocio">
                </div>
                <small class="ms-hint">Esta será la dirección que compartirás con tus clientes. Se guarda con "Guardar borrador".</small>
                <p class="ms-hint" *ngIf="canCustomizeSite() && !canCustomizeSlug()">Tu enlace se genera automáticamente desde el nombre de tu negocio. <button type="button" class="ms-link-btn" (click)="goToSubscription()">Actualiza a Premium para personalizarlo.</button></p>
              </label>
            </ng-container>

            <!-- Portada -->
            <ng-container *ngIf="activeSection()==='portada'">
              <h2>Portada</h2>
              <p class="ms-section-hint">La imagen y el mensaje principal que ven tus clientes al entrar.</p>

              <div class="ms-field">
                <span class="ms-field-label">Imagen de portada <app-photo-tips kind="banner"></app-photo-tips></span>
                <div class="ms-banner-row">
                  <div class="ms-banner-preview" *ngIf="bannerPreview() || site.bannerUrl as previewUrl2" [style.background-image]="'url(' + previewUrl2 + ')'"></div>
                  <div class="ms-banner-preview ms-banner-empty" *ngIf="!bannerPreview() && !site.bannerUrl"><i class="bi bi-image" aria-hidden="true"></i><span>Sin imagen</span></div>
                  <div class="ms-banner-actions">
                    <input type="file" id="miSitioBannerInput" accept="image/png,image/jpeg,image/jpg,image/webp,image/gif" [disabled]="!canCustomizeSite()" (change)="onBannerSelected($event)" class="ms-file-input">
                    <label for="miSitioBannerInput" class="ms-btn ms-btn-small ms-btn-ghost"><i class="bi bi-cloud-arrow-up" aria-hidden="true"></i> Seleccionar imagen</label>
                    <button type="button" *ngIf="selectedBanner()" class="ms-link-btn" (click)="cancelarBanner()">Quitar selección</button>
                  </div>
                </div>
                <small class="ms-hint">Formatos: PNG, JPG, WEBP, GIF. Máx. 5MB. Se guarda con "Guardar borrador".</small>
              </div>

              <label class="ms-field">
                <span class="ms-field-label">Título principal<small>{{ heroTitleDraft().length }}/150</small></span>
                <input type="text" [value]="heroTitleDraft()" (input)="heroTitleDraft.set($any($event.target).value)" maxlength="150" [disabled]="!canCustomizeSite()" placeholder="Encuentra el lugar ideal para trabajar">
              </label>
              <label class="ms-field">
                <span class="ms-field-label">Subtítulo<small>{{ heroSubtitleDraft().length }}/300</small></span>
                <textarea rows="2" [value]="heroSubtitleDraft()" (input)="heroSubtitleDraft.set($any($event.target).value)" maxlength="300" [disabled]="!canCustomizeSite()" placeholder="Salas de reunión, oficinas privadas y espacios de coworking elegidos para impulsar tus mejores ideas."></textarea>
              </label>
              <label class="ms-field">
                <span class="ms-field-label">Texto del botón principal<small>{{ ctaTextDraft().length }}/40</small></span>
                <input type="text" [value]="ctaTextDraft()" (input)="ctaTextDraft.set($any($event.target).value)" maxlength="40" [disabled]="!canCustomizeSite()" placeholder="Ver espacios">
                <small class="ms-hint">El botón siempre lleva a la lista de espacios de tu negocio.</small>
              </label>
            </ng-container>

            <!-- Colores -->
            <ng-container *ngIf="activeSection()==='colores'">
              <h2>Colores y estilo</h2>
              <p class="ms-section-hint">Se aplican a todo tu sitio (fondo, botones, barra de navegación y textos en todas sus páginas), solo en el tema claro; el tema oscuro se mantiene fijo.</p>

              <div class="ms-color-grid">
                <label class="ms-color-card">
                  <span class="ms-color-swatch" [style.background]="backgroundColorDraft()"></span>
                  <span class="ms-color-info">
                    <span class="ms-field-label">Fondo del sistema</span>
                    <small>Color de fondo de todas las páginas de tu sitio.</small>
                  </span>
                  <input type="color" [value]="backgroundColorDraft()" (input)="backgroundColorDraft.set($any($event.target).value)" [disabled]="!canCustomizeSite()">
                </label>
                <label class="ms-color-card">
                  <span class="ms-color-swatch" [style.background]="primaryColorDraft()"></span>
                  <span class="ms-color-info">
                    <span class="ms-field-label">Botones primarios</span>
                    <small>Botón principal, enlaces activos y foco de campos.</small>
                  </span>
                  <input type="color" [value]="primaryColorDraft()" (input)="primaryColorDraft.set($any($event.target).value)" [disabled]="!canCustomizeSite()">
                </label>
                <label class="ms-color-card">
                  <span class="ms-color-swatch" [style.background]="secondaryColorDraft()"></span>
                  <span class="ms-color-info">
                    <span class="ms-field-label">Botones secundarios</span>
                    <small>Acompaña al color primario en degradados y acentos.</small>
                  </span>
                  <input type="color" [value]="secondaryColorDraft()" (input)="secondaryColorDraft.set($any($event.target).value)" [disabled]="!canCustomizeSite()">
                </label>
                <label class="ms-color-card">
                  <span class="ms-color-swatch" [style.background]="navbarColorDraft()"></span>
                  <span class="ms-color-info">
                    <span class="ms-field-label">Barra de navegación</span>
                    <small>Fondo de la barra superior de tu sitio.</small>
                  </span>
                  <input type="color" [value]="navbarColorDraft()" (input)="navbarColorDraft.set($any($event.target).value)" [disabled]="!canCustomizeSite()">
                </label>
                <label class="ms-color-card">
                  <span class="ms-color-swatch" [style.background]="highlightTextColorDraft()"></span>
                  <span class="ms-color-info">
                    <span class="ms-field-label">Textos sobresalientes</span>
                    <small>Títulos y textos destacados de tu página.</small>
                  </span>
                  <input type="color" [value]="highlightTextColorDraft()" (input)="highlightTextColorDraft.set($any($event.target).value)" [disabled]="!canCustomizeSite()">
                </label>
              </div>
              <button type="button" class="ms-btn ms-btn-small ms-btn-ghost" [disabled]="!canCustomizeSite()" (click)="restablecerColores()">
                <i class="bi bi-arrow-counterclockwise" aria-hidden="true"></i> Restablecer valores predeterminados
              </button>
            </ng-container>

            <!-- Información -->
            <ng-container *ngIf="activeSection()==='informacion'">
              <h2>Información del negocio</h2>
              <p class="ms-section-hint">Se muestra en la sección "Sobre nosotros" y "Contacto" de tu sitio.</p>

              <label class="ms-field">
                <span class="ms-field-label">Sobre nosotros<small>{{ aboutTextDraft().length }}/2000</small></span>
                <textarea rows="5" [value]="aboutTextDraft()" (input)="aboutTextDraft.set($any($event.target).value)" maxlength="2000" [disabled]="!canCustomizeSite()" placeholder="Contales a tus clientes quién sos y qué hace especial a tu negocio."></textarea>
              </label>
              <label class="ms-field">
                <span class="ms-field-label">Dirección<small>{{ addressDraft().length }}/255</small></span>
                <input type="text" [value]="addressDraft()" (input)="addressDraft.set($any($event.target).value)" maxlength="255" [disabled]="!canCustomizeSite()" placeholder="Torre Cuscatlán, Piso 7, San Salvador">
              </label>
              <div class="ms-field-row ms-field-row-top">
                <label class="ms-field">
                  <span class="ms-field-label">Teléfono de contacto</span>
                  <input type="text" [value]="phoneDraft()" (input)="phoneDraft.set($any($event.target).value)" maxlength="20" [disabled]="!canCustomizeSite()" placeholder="70000000">
                </label>
                <div class="ms-field">
                  <span class="ms-field-label">Correo de contacto</span>
                  <input type="text" [value]="emailDraft()" (input)="emailDraft.set($any($event.target).value)" [disabled]="!canCustomizeSite()" placeholder="contacto@tunegocio.com">
                  <button type="button" *ngIf="emailChanged()" class="ms-btn ms-btn-small ms-field-inline-btn" [disabled]="isSavingEmail()" (click)="guardarCorreoContacto()">
                    {{ isSavingEmail() ? 'Guardando...' : 'Guardar correo' }}
                  </button>
                </div>
              </div>
              <small class="ms-hint">Teléfono se guarda con "Guardar borrador". El correo pide tu contraseña porque es información con la que un cliente podría contactarte.</small>
            </ng-container>

            <!-- Secciones -->
            <ng-container *ngIf="activeSection()==='secciones'">
              <h2>Secciones visibles</h2>
              <p class="ms-section-hint">Elige qué partes de tu sitio público se muestran a tus clientes.</p>

              <div class="ms-toggle-row" *ngFor="let item of sectionToggles">
                <div class="ms-toggle-label">
                  <span class="ms-toggle-icon"><i [class]="item.icon" aria-hidden="true"></i></span>
                  <div>
                    <strong>{{ item.label }}</strong>
                    <small>{{ item.hint }}</small>
                  </div>
                </div>
                <button type="button" class="ms-switch" [class.on]="visibleSectionsDraft()[item.key]" [disabled]="!canCustomizeSite()" (click)="toggleSection(item.key)" [attr.aria-pressed]="visibleSectionsDraft()[item.key]" [attr.aria-label]="item.label">
                  <span></span>
                </button>
              </div>
            </ng-container>

            <!-- Redes sociales: mismo editor (lista ordenable, buscador de redes y
                 vista previa) que las redes oficiales de la plataforma, en modo
                 "business": se guarda con el borrador y se publica con el sitio. -->
            <ng-container *ngIf="activeSection()==='redes'">
              <h2>Redes sociales</h2>
              <p class="ms-section-hint">Agrega, ordena y oculta las redes de tu negocio. Se muestran como íconos en la sección de contacto de tu sitio.</p>

              <app-platform-social-links-admin
                mode="business"
                [draftLinks]="socialLinksDraft()"
                [savedLinks]="site.socialLinks || []"
                [catalogOptions]="site.socialCatalog || []"
                [suggestedOptions]="site.socialSuggested || []"
                [limitValue]="site.socialLimit || 12"
                [externalSaving]="isSavingDraft() || isPublishing()"
                [externalError]="socialLinksError()"
                (draftChange)="onSocialLinksDraftChange($event)"
                (saveRequest)="canCustomizeSite() ? guardarBorrador(true) : showUpgradePrompt()"
              ></app-platform-social-links-admin>
            </ng-container>

            <!-- Vista de enlace -->
            <ng-container *ngIf="activeSection()==='vista-enlace'">
              <h2>Vista de enlace</h2>
              <p class="ms-section-hint">Así se ve tu sitio cuando alguien comparte su enlace en WhatsApp, Facebook o redes sociales, independiente de la portada de tu sitio.</p>

              <div class="ms-field">
                <span class="ms-field-label">Foto para compartir <app-photo-tips kind="share"></app-photo-tips></span>
                <div class="ms-banner-row">
                  <div class="ms-banner-preview" *ngIf="shareImagePreview() || site.shareImageUrl as previewUrl3" [style.background-image]="'url(' + previewUrl3 + ')'"></div>
                  <div class="ms-banner-preview ms-banner-empty" *ngIf="!shareImagePreview() && !site.shareImageUrl"><i class="bi bi-image" aria-hidden="true"></i><span>Sin imagen</span></div>
                  <div class="ms-banner-actions">
                    <input type="file" id="miSitioShareImageInput" accept="image/png,image/jpeg,image/jpg,image/webp,image/gif" [disabled]="!canCustomizeSite()" (change)="onShareImageSelected($event)" class="ms-file-input">
                    <label for="miSitioShareImageInput" class="ms-btn ms-btn-small ms-btn-ghost"><i class="bi bi-cloud-arrow-up" aria-hidden="true"></i> Seleccionar imagen</label>
                    <button type="button" *ngIf="selectedShareImage()" class="ms-link-btn" (click)="cancelarShareImage()">Quitar selección</button>
                  </div>
                </div>
                <small class="ms-hint">Cualquier tamaño sirve: se recorta automáticamente a 1200×630, la medida que usan WhatsApp y Facebook. Se guarda con "Guardar borrador".</small>
              </div>

              <label class="ms-field">
                <span class="ms-field-label">Título para compartir<small>{{ shareTitleDraft().length }}/70</small></span>
                <input type="text" [value]="shareTitleDraft()" (input)="shareTitleDraft.set($any($event.target).value)" maxlength="70" [disabled]="!canCustomizeSite()" placeholder="{{ site.heroTitle || (currentBusiness()?.name) || 'Encuentra el lugar ideal para trabajar' }}">
                <small class="ms-hint">Si lo dejas vacío, se usa el nombre de tu negocio.</small>
              </label>
              <label class="ms-field">
                <span class="ms-field-label">Descripción para compartir<small>{{ shareDescriptionDraft().length }}/200</small></span>
                <textarea rows="3" [value]="shareDescriptionDraft()" (input)="shareDescriptionDraft.set($any($event.target).value)" maxlength="200" [disabled]="!canCustomizeSite()" placeholder="Salas de reunión, oficinas privadas y espacios de coworking para impulsar tus ideas."></textarea>
                <small class="ms-hint">Si la dejas vacía, se usa una descripción genérica de TuSala.</small>
              </label>
            </ng-container>

            <!-- Acceso privado -->
            <ng-container *ngIf="activeSection()==='privado'">
              <ng-container *ngIf="canUsePrivateAccess(); else privateAccessLocked">
                <h2>Acceso privado</h2>
                <p class="ms-section-hint">Decide qué ve cada visitante. Lo privado no aparece en la galería y pide el código de 4 dígitos para verlo y reservarlo.</p>

                <div class="ms-privacy-modes">
                  <div class="ms-privacy-mode" [class.active]="privacyInfo()?.isPrivate">
                    <span class="ms-toggle-icon"><i class="bi bi-buildings" aria-hidden="true"></i></span>
                    <div class="ms-privacy-mode-copy">
                      <strong>Todo el negocio</strong>
                      <small>{{ privacyInfo()?.isPrivate ? 'Activo: tu sitio y todos tus espacios piden el código.' : 'Desactivado: tu negocio se ve en la galería como cualquier otro.' }}</small>
                    </div>
                    <button type="button" class="ms-switch" [class.on]="privacyInfo()?.isPrivate" [disabled]="isTogglingPrivacy()" (click)="togglePrivacy(!privacyInfo()?.isPrivate)" [attr.aria-pressed]="privacyInfo()?.isPrivate" aria-label="Negocio privado">
                      <span></span>
                    </button>
                  </div>
                  <div class="ms-privacy-mode" [class.active]="!privacyInfo()?.isPrivate && privateSpacesCount() > 0">
                    <span class="ms-toggle-icon"><i class="bi bi-door-closed" aria-hidden="true"></i></span>
                    <div class="ms-privacy-mode-copy">
                      <strong>Espacios puntuales</strong>
                      <small *ngIf="privacyInfo()?.isPrivate">Con todo el negocio privado, cada espacio ya pide el código.</small>
                      <small *ngIf="!privacyInfo()?.isPrivate && privateSpacesCount() > 0">{{ privateSpacesCount() === 1 ? '1 espacio privado' : privateSpacesCount() + ' espacios privados' }}; el resto sigue público. Solo tú ves esta cifra.</small>
                      <small *ngIf="!privacyInfo()?.isPrivate && privateSpacesCount() === 0">Privatiza una sala sin afectar el resto con el ícono <i class="bi bi-shield-lock" aria-label="escudo"></i> en "Gestión de salas".</small>
                    </div>
                    <button type="button" class="ms-btn ms-btn-small ms-btn-ghost" (click)="goToSpaces()">Gestionar</button>
                  </div>
                </div>

                <ng-container *ngIf="privacyInfo()?.accessCode as code; else noAccessCode">
                  <div class="ms-access-code-card">
                    <div class="ms-access-code-copy">
                      <span class="ms-field-label">Código de acceso</span>
                      <strong class="ms-access-code">{{ code }}</strong>
                      <small class="ms-hint">Es el mismo para todo tu negocio y para cada espacio privado. Compártelo tú (llamada, WhatsApp o en persona): TuSala nunca lo envía.</small>
                    </div>
                    <button type="button" class="ms-btn ms-btn-small ms-btn-ghost" [disabled]="isRegeneratingCode()" (click)="regenerateAccessCode()"><i class="bi bi-arrow-repeat" aria-hidden="true"></i> {{ isRegeneratingCode() ? 'Generando...' : 'Generar nuevo código' }}</button>
                  </div>

                  <div class="ms-field">
                    <span class="ms-field-label">Compartir acceso</span>
                    <div class="ms-banner-actions">
                      <button type="button" class="ms-btn ms-btn-small ms-btn-ghost" [disabled]="isCopyingAccessLink()" (click)="copyAccessLink()"><i class="bi bi-clipboard" aria-hidden="true"></i> Copiar acceso</button>
                      <button type="button" class="ms-btn ms-btn-small ms-btn-ghost" [disabled]="isSharingAccessLink()" (click)="shareAccessLink()"><i class="bi bi-share" aria-hidden="true"></i> Compartir acceso</button>
                      <button type="button" class="ms-btn ms-btn-small ms-btn-ghost" (click)="openInvitationEditor(null)"><i class="bi bi-image" aria-hidden="true"></i> Imagen para imprimir</button>
                      <button type="button" class="ms-btn ms-btn-small ms-btn-ghost" (click)="downloadAccessQr()"><i class="bi bi-qr-code" aria-hidden="true"></i> Descargar solo QR</button>
                    </div>
                    <small class="ms-hint">"Copiar" y "Compartir" envían el enlace y el código como texto aparte; el código nunca va dentro del enlace. La imagen y el QR llevan a la pantalla donde el visitante escribe el código.</small>
                  </div>
                </ng-container>
                <ng-template #noAccessCode>
                  <p class="ms-hint ms-access-code-empty"><i class="bi bi-info-circle" aria-hidden="true"></i> Tu código de acceso se crea al activar "Todo el negocio" o al privatizar tu primer espacio.</p>
                </ng-template>

                <label class="ms-field">
                  <span class="ms-field-label">Título de la pantalla de acceso<small>{{ accessWelcomeTitleDraft().length }}/150</small></span>
                  <input type="text" [value]="accessWelcomeTitleDraft()" (input)="accessWelcomeTitleDraft.set($any($event.target).value)" maxlength="150" placeholder="Bienvenido a {{ currentBusiness()?.name }}">
                </label>
                <label class="ms-field">
                  <span class="ms-field-label">Mensaje de la pantalla de acceso<small>{{ accessWelcomeMessageDraft().length }}/500</small></span>
                  <textarea rows="4" [value]="accessWelcomeMessageDraft()" (input)="accessWelcomeMessageDraft.set($any($event.target).value)" maxlength="500" placeholder="Ingresa el código que te compartimos para reservar nuestros espacios."></textarea>
                </label>
                <small class="ms-hint">Usa el mismo logo, banner y colores que ya configuraste en "Portada" y "Colores y estilo". Se guarda con "Guardar borrador" y tus visitantes lo ven al "Publicar cambios".</small>
              </ng-container>
              <ng-template #privateAccessLocked>
                <h2>Acceso privado</h2>
                <p class="ms-section-hint">Tu plan actual no incluye esta función.</p>
                <button type="button" class="ms-btn ms-btn-primary" (click)="showUpgradePrompt()">Ver planes</button>
              </ng-template>
            </ng-container>
          </div>

          <div class="ms-preview" #previewPanel *ngIf="activeSection()!=='vista-enlace'; else sharePreviewPanel">
            <div class="ms-preview-header">
              <span><i class="bi bi-eye" aria-hidden="true"></i> Vista previa de tu sitio</span>
              <div class="ms-device-toggle">
                <button type="button" [class.active]="previewDevice()==='desktop'" (click)="previewDevice.set('desktop')"><i class="bi bi-laptop" aria-hidden="true"></i> Laptop</button>
                <button type="button" [class.active]="previewDevice()==='mobile'" (click)="previewDevice.set('mobile')"><i class="bi bi-phone" aria-hidden="true"></i> Móvil</button>
              </div>
            </div>

            <div class="ms-mock-wrap" *ngIf="safePreviewUrl() as url; else previewEmpty">
              <div class="ms-mock ms-mock-laptop" *ngIf="previewDevice()==='desktop'">
                <div class="ms-mock-screen">
                  <span class="ms-mock-cam" aria-hidden="true"></span>
                  <div class="ms-mock-viewport">
                    <div class="ms-mock-scaled" [style.width.px]="laptopScaledWidth()" [style.height.px]="laptopScaledHeight()">
                      <iframe #previewIframe [src]="url" title="Vista previa de tu sitio (laptop)" (load)="onPreviewIframeLoad()"
                        [style.width.px]="laptopIframeWidth" [style.height.px]="laptopIframeHeight"
                        [style.transform]="'scale(' + laptopScale() + ')'"></iframe>
                    </div>
                  </div>
                </div>
                <div class="ms-mock-base"><span class="ms-mock-hinge"></span></div>
              </div>

              <div class="ms-mock ms-mock-phone" *ngIf="previewDevice()==='mobile'">
                <span class="ms-mock-notch" aria-hidden="true"></span>
                <div class="ms-mock-phone-screen">
                  <iframe #previewIframe [src]="url" title="Vista previa de tu sitio (móvil)" (load)="onPreviewIframeLoad()" [style.height.px]="phoneIframeHeight"></iframe>
                </div>
                <span class="ms-mock-home-bar" aria-hidden="true"></span>
              </div>
            </div>
            <ng-template #previewEmpty>
              <div class="ms-preview-empty">
                <i class="bi bi-globe2" aria-hidden="true"></i>
                <p>{{ canCustomizeSite() ? 'Guarda tu enlace para ver la vista previa.' : 'La vista previa no está disponible en tu plan actual.' }}</p>
              </div>
            </ng-template>
          </div>

          <!-- Vista de enlace: tarjeta como se ve al compartir en WhatsApp/Facebook/etc.
               Se actualiza al instante con lo que se escribe/selecciona -- sin
               esperar a guardar ni al servidor, para que sea inmediata. -->
          <ng-template #sharePreviewPanel>
            <div class="ms-preview">
              <div class="ms-preview-header">
                <span><i class="bi bi-eye" aria-hidden="true"></i> Vista previa al compartir</span>
              </div>
              <div class="ms-share-card-wrap">
                <div class="ms-share-card">
                  <div class="ms-share-card-image" [style.background-image]="'url(' + (shareImagePreview() || site.shareImageUrl || (privacyInfo()?.isPrivate ? privateShareCardImage : (site.bannerUrl || shareCardFallbackImage))) + ')'"></div>
                  <div class="ms-share-card-body">
                    <span class="ms-share-card-domain">{{ shareCardDomain }}</span>
                    <strong class="ms-share-card-title">{{ shareTitleDraft().trim() || site.heroTitle || currentBusiness()?.name || 'TuSala' }}</strong>
                    <p class="ms-share-card-desc">{{ shareDescriptionDraft().trim() || site.shortDescription || 'Encuentra y reserva salas, oficinas y espacios para tus reuniones y eventos, con pago seguro en línea.' }}</p>
                  </div>
                </div>
                <p class="ms-hint ms-share-card-hint" *ngIf="usingPrivateFallbackImage(site)"><i class="bi bi-shield-lock" aria-hidden="true"></i> Tu negocio es privado: se usa la imagen de acceso exclusivo en vez de la genérica, para que quien la reciba entienda que necesita un código. Sube tu propia imagen arriba si prefieres reemplazarla.</p>
                <p class="ms-hint ms-share-card-hint"><i class="bi bi-info-circle" aria-hidden="true"></i> Es una simulación orientativa: cada app (WhatsApp, Facebook, etc.) la recorta ligeramente distinto.</p>
              </div>
            </div>
          </ng-template>
        </div>
      </div>
    </div>

    <app-invitation-editor
      *ngIf="showInvitationEditor() && privacyInfo()?.accessCode as code"
      [url]="privacyInfo()?.url || mySiteUrl() || ''"
      [code]="code"
      [businessName]="currentBusiness()?.name || ''"
      [initialPrimaryColor]="primaryColorDraft()"
      [initialSecondaryColor]="secondaryColorDraft()"
      [spaces]="privateAccessSpaces()"
      [initialSpaceId]="initialInvitationSpaceId()"
      (close)="showInvitationEditor.set(false)">
    </app-invitation-editor>
  `,
  styles: [`
    :host { display: block; }
    .mi-sitio-page { display: grid; gap: 1.2rem; }
    .ms-header { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 1rem; }
    .ms-header-actions { display: flex; flex-wrap: wrap; align-items: center; gap: .7rem; }
    .ms-status { display: grid; gap: .1rem; margin-right: .3rem; }
    .ms-status-badge { display: inline-flex; align-items: center; gap: .35rem; width: fit-content; padding: .3rem .65rem; border-radius: 999px; color: var(--ts-warning-text); background: var(--ts-bg-orange-450-a12); border: 1px solid var(--ts-border-orange-450-a30); font-size: .72rem; font-weight: 800; }
    .ms-status-badge i { font-size: .5rem; }
    .ms-status-badge.is-published { color: var(--ts-success-text); background: var(--ts-bg-green-500-a12); border-color: var(--ts-border-green-500-a30); }
    .ms-status small { color: var(--ts-text-muted); font-size: .68rem; }
    .ms-btn { display: inline-flex; align-items: center; gap: .45rem; min-height: 40px; padding: 0 1rem; border: 1px solid var(--ts-border-strong); border-radius: 10px; color: var(--ts-text); background: var(--ts-surface); font: inherit; font-size: .8rem; font-weight: 700; cursor: pointer; transition: background .15s ease, border-color .15s ease; }
    .ms-btn:hover:not(:disabled) { background: var(--ts-surface-hover); border-color: var(--ts-primary); }
    .ms-btn:disabled { opacity: .5; cursor: not-allowed; }
    .ms-btn-ghost { background: transparent; }
    .ms-btn-primary { color: var(--ts-text-on-accent); border-color: transparent; background: linear-gradient(135deg, var(--ts-primary), var(--ts-indigo)); }
    .ms-btn-primary:hover:not(:disabled) { filter: brightness(1.08); }
    .ms-btn-small { min-height: 36px; padding: 0 .8rem; font-size: .76rem; flex: 0 0 auto; }
    .ms-link-btn { border: 0; padding: 0; background: transparent; color: var(--ts-primary); font: inherit; font-size: .74rem; font-weight: 700; cursor: pointer; text-decoration: underline; }

    .ms-premium-lock { grid-column: 1 / -1; display: flex; align-items: center; gap: 1rem; padding: 1rem 1.2rem; border: 1px solid var(--ts-border-orange-450-a30); border-radius: 14px; background: var(--ts-bg-orange-450-a8-2); }
    .ms-premium-lock i { font-size: 1.4rem; color: var(--ts-warning-text); }
    .ms-premium-lock strong { color: var(--ts-text); }
    .ms-premium-lock p { margin: .15rem 0 0; color: var(--ts-text-muted); font-size: .8rem; }
    .ms-premium-lock button { flex: 0 0 auto; margin-left: auto; padding: .6rem 1rem; border: 0; border-radius: 10px; color: var(--ts-text-on-accent); background: linear-gradient(135deg, var(--ts-primary), var(--ts-indigo)); font-weight: 800; font-size: .78rem; cursor: pointer; }

    .ms-body { display: grid; gap: 1.2rem; }

    /* Barra de secciones en una sola línea horizontal en escritorio (antes
     * era una columna lateral fija): ahorra ancho para el formulario y la
     * vista previa, y no se ve saturada porque cabe todo en una fila -- con
     * scroll horizontal propio si no entra completa. En tablet/móvil (ver
     * media query más abajo) pasa a una grilla de 2 filas fijas en vez de
     * scroll: las 6 pestañas quedan siempre visibles de un vistazo, sin
     * tener que deslizar para descubrir que hay más. */
    .ms-subnav { display: flex; align-items: center; gap: .3rem; padding: .4rem; border: 1px solid var(--ts-border-strong); border-radius: 14px; background: var(--ts-bg-navy-850-2); overflow-x: auto; scrollbar-width: none; -ms-overflow-style: none; }
    .ms-subnav::-webkit-scrollbar { display: none; }
    .ms-subnav-item { display: inline-flex; align-items: center; gap: .5rem; flex: 0 0 auto; min-height: 38px; padding: 0 .8rem; border: 0; border-radius: 9px; color: var(--ts-text-muted); background: transparent; font: inherit; font-size: .78rem; font-weight: 700; white-space: nowrap; text-align: left; cursor: pointer; }
    .ms-subnav-item i { width: 16px; line-height: 1; font-size: .92rem; text-align: center; flex: 0 0 auto; }
    .ms-subnav-item:hover { color: var(--ts-text); background: var(--ts-bg-gray-0-a4); }
    .ms-subnav-item.active { color: var(--ts-text-on-accent); background: linear-gradient(135deg, var(--ts-primary), var(--ts-indigo)); }

    .ms-columns { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 560px); align-items: start; gap: 1.2rem; }

    .ms-form { position: relative; display: grid; align-content: start; gap: 1rem; padding: 1.1rem 1.2rem; border: 1px solid var(--ts-border-strong); border-radius: 14px; background: var(--ts-bg-navy-850-2); min-width: 0; }
    .ms-form.is-locked textarea, .ms-form.is-locked input:not([disabled]) { opacity: .7; }
    .ms-locked-overlay { position: absolute; inset: 0; z-index: 5; padding: 0; border: 0; background: transparent; cursor: pointer; }
    .ms-form h2 { margin: 0; color: var(--ts-text); font-size: 1.05rem; }
    .ms-section-hint { margin: -.4rem 0 0; color: var(--ts-text-muted); font-size: .78rem; }
    .ms-field { display: grid; gap: .35rem; min-width: 0; }
    .ms-field-label { display: flex; align-items: center; justify-content: space-between; gap: .5rem; color: var(--ts-text-sky-350-3); font-size: .68rem; font-weight: 800; letter-spacing: .04em; text-transform: uppercase; }
    .ms-field-label small { color: var(--ts-text-sky-450-11); font-size: .64rem; font-weight: 700; text-transform: none; letter-spacing: 0; }
    .ms-field input[type="text"], .ms-field textarea { min-height: 42px; padding: .55rem .7rem; border: 1px solid var(--ts-border-strong); border-radius: 8px; color: var(--ts-text-soft-2); background: var(--ts-scrollbar-track); font: inherit; font-size: .84rem; box-sizing: border-box; width: 100%; resize: vertical; }
    .ms-field input:disabled, .ms-field textarea:disabled { color: var(--ts-text-sky-400-18); cursor: not-allowed; }
    .ms-field-row { display: flex; align-items: flex-end; gap: .8rem; }
    .ms-field-row .ms-field { flex: 1; }
    /* Fila donde una columna puede crecer más que la otra (p. ej. correo con
     * su botón "Guardar" debajo del input) -- alinear por abajo (el default)
     * empujaría el input más corto hacia abajo para emparejar el fondo,
     * dejando sus etiquetas a distinta altura ("uno sobre otro"). */
    .ms-field-row-top { align-items: flex-start; }
    .ms-field-inline-btn { justify-self: start; margin-top: .1rem; }
    .ms-error { margin: 0; color: var(--ts-text-red-250-7); font-size: .76rem; }
    .ms-hint { margin: 0; color: var(--ts-text-sky-350-2); font-size: .74rem; }

    /* Mismo patrón que .ms-slug-row (ícono/prefijo y el input como cajas
     * hermanas en un flex, no superpuestas): un ícono posicionado "encima"
     * del texto con position:absolute + padding-left en el input es frágil
     * (dos reglas en archivos/momentos distintos tienen que coincidir en el
     * mismo número exacto) -- con cajas flex hermanas es geométricamente
     * imposible que el ícono quede sobre el placeholder. */
    .ms-input-icon { display: flex; align-items: stretch; border: 1px solid var(--ts-border-strong); border-radius: 8px; background: var(--ts-scrollbar-track); overflow: hidden; }
    .ms-input-icon-glyph { display: grid; place-items: center; width: 2.4rem; flex: 0 0 auto; border-right: 1px solid var(--ts-border-strong); color: var(--ts-text-sky-350-3); }
    .ms-input-icon-glyph i { width: 16px; height: 16px; line-height: 1; font-size: .92rem; text-align: center; }
    .ms-input-icon input[type="text"] { flex: 1; min-width: 0; border: 0; border-radius: 0; background: transparent; }
    .ms-input-icon input[type="text"]:focus { outline: none; }

    .ms-color-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: .7rem; }
    .ms-color-card { position: relative; display: flex; align-items: center; gap: .7rem; padding: .65rem .8rem; border: 1px solid var(--ts-border-strong); border-radius: 10px; background: var(--ts-scrollbar-track); cursor: pointer; }
    .ms-color-swatch { width: 34px; height: 34px; flex: 0 0 auto; border-radius: 9px; border: 1px solid var(--ts-border-gray-0-a16); box-shadow: inset 0 0 0 3px var(--ts-shadow-gray-1000-a18); }
    .ms-color-info { display: grid; gap: .1rem; min-width: 0; }
    .ms-color-info .ms-field-label { text-transform: none; letter-spacing: 0; font-size: .78rem; color: var(--ts-text); }
    .ms-color-info small { color: var(--ts-text-sky-450-11); font-size: .68rem; font-weight: 600; }
    .ms-color-card input[type="color"] { position: absolute; inset: 0; width: 100%; height: 100%; padding: 0; border: 0; opacity: 0; cursor: pointer; }
    .ms-color-card:has(input:disabled) { cursor: not-allowed; opacity: .6; }

    .ms-logo-row, .ms-banner-row { display: flex; flex-wrap: wrap; align-items: center; gap: 1rem; }
    .ms-access-code { font-size: 1.9rem; font-weight: 800; letter-spacing: .35rem; padding: .6rem 1.1rem; border: 1px dashed var(--ts-border-strong); border-radius: 10px; background: var(--ts-scrollbar-track); color: var(--ts-text); }
    .ms-logo-preview { display: grid; place-items: center; width: 104px; height: 104px; flex: 0 0 auto; border: 1px solid var(--ts-border-strong); border-radius: 14px; background: var(--ts-scrollbar-track); overflow: hidden; }
    .ms-logo-preview img { width: 100%; height: 100%; object-fit: contain; }
    .ms-logo-empty { color: var(--ts-text-sky-450-11); font-size: 1.8rem; }
    .ms-logo-actions, .ms-banner-actions { display: flex; flex-wrap: wrap; align-items: center; gap: .6rem; }
    .ms-file-input { position: absolute; width: 1px; height: 1px; overflow: hidden; opacity: 0; }
    .ms-banner-preview { width: 190px; height: 100px; flex: 0 0 auto; border: 1px solid var(--ts-border-strong); border-radius: 10px; background-position: center; background-size: cover; background-repeat: no-repeat; }
    .ms-banner-empty { display: grid; place-items: center; gap: .2rem; color: var(--ts-text-sky-450-11); background: var(--ts-scrollbar-track); font-size: .68rem; }
    .ms-banner-empty i { font-size: 1.3rem; }

    .ms-slug-row { display: flex; align-items: center; gap: 0; border: 1px solid var(--ts-border-strong); border-radius: 8px; background: var(--ts-scrollbar-track); overflow: hidden; }
    .ms-slug-prefix { padding: 0 0 0 .7rem; color: var(--ts-text-sky-450-11); font-size: .82rem; white-space: nowrap; }
    .ms-slug-row input[type="text"] { border: 0; border-radius: 0; background: transparent; }
    .ms-slug-row input[type="text"]:focus { outline: none; }

    /* Acceso privado: los dos modos como tarjetas con su estado real. */
    .ms-privacy-modes { display: grid; gap: .65rem; }
    .ms-privacy-mode { display: flex; align-items: center; gap: .8rem; padding: .85rem 1rem; border: 1px solid var(--ts-border-strong); border-radius: 14px; transition: border-color .2s ease, background .2s ease; }
    .ms-privacy-mode.active { border-color: var(--ts-primary); background: var(--ts-bg-blue-450-a12); }
    .ms-privacy-mode-copy { flex: 1; min-width: 0; display: grid; gap: .15rem; }
    .ms-privacy-mode-copy strong { color: var(--ts-text); font-size: .86rem; }
    .ms-privacy-mode-copy small { color: var(--ts-text-muted); font-size: .74rem; line-height: 1.45; }
    .ms-privacy-mode > .ms-switch, .ms-privacy-mode > .ms-btn { flex: none; }
    .ms-access-code-card { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: .9rem 1rem; padding: 1rem; border: 1px dashed var(--ts-border-strong); border-radius: 14px; }
    .ms-access-code-copy { display: grid; gap: .45rem; min-width: 0; flex: 1 1 260px; }
    .ms-access-code-copy .ms-access-code { width: fit-content; }
    .ms-access-code-empty { display: flex; align-items: flex-start; gap: .4rem; padding: .75rem .9rem; border: 1px dashed var(--ts-border-strong); border-radius: 12px; }
    @media (max-width: 520px) { .ms-privacy-mode { flex-wrap: wrap; } .ms-privacy-mode-copy { flex-basis: calc(100% - 50px); } .ms-privacy-mode > .ms-btn { margin-left: auto; } }
    .ms-toggle-row { display: flex; align-items: center; justify-content: space-between; gap: 1rem; padding: .8rem 0; border-bottom: 1px solid var(--ts-border); }
    .ms-toggle-row:last-child { border-bottom: 0; }
    .ms-toggle-label { display: flex; align-items: center; gap: .7rem; min-width: 0; }
    .ms-toggle-icon { display: grid; place-items: center; width: 34px; height: 34px; flex: 0 0 auto; border-radius: 9px; color: var(--ts-primary); background: var(--ts-bg-blue-450-a12); }
    .ms-toggle-icon i { width: 17px; height: 17px; line-height: 1; font-size: 1rem; text-align: center; }
    .ms-toggle-row strong { display: block; color: var(--ts-text); font-size: .86rem; }
    .ms-toggle-row small { display: block; margin-top: .1rem; color: var(--ts-text-muted); font-size: .74rem; }
    /* min-height explícito a propósito: una regla global de styles.css le
     * pone min-height:var(--control-height) (40px) a TODO <button> dentro
     * de app-dashboard -- de donde cuelga app-mi-sitio -- y como este botón
     * nunca declaraba su propio min-height, ese 40px ganaba sin competencia
     * (no hay conflicto de especificidad que resolver: si no se fija la
     * propiedad acá, no hay nada con qué pisarla) y estiraba el interruptor
     * de una píldora compacta de 26px a un óvalo de 40px. */
    .ms-switch { position: relative; flex: 0 0 auto; width: 44px; height: 26px; min-height: 26px; padding: 0; border: 1px solid var(--ts-border-strong); border-radius: 999px; background: var(--ts-scrollbar-track); cursor: pointer; transition: background .15s ease, border-color .15s ease; }
    .ms-switch span { position: absolute; top: 2px; left: 2px; width: 20px; height: 20px; border-radius: 50%; background: var(--ts-bg-sky-450-4); transition: transform .18s ease, background .15s ease; }
    .ms-switch.on { border-color: var(--ts-primary); background: var(--ts-primary); }
    .ms-switch.on span { transform: translateX(18px); background: var(--ts-bg-gray-0); }
    .ms-switch:disabled { opacity: .5; cursor: not-allowed; }

    .ms-preview { position: sticky; top: 12px; display: grid; align-content: start; gap: .7rem; padding: 1rem; border: 1px dashed var(--ts-border-strong); border-radius: 14px; background: var(--ts-modal-b); min-width: 0; }
    .ms-preview-header { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: .6rem; color: var(--ts-text-sky-350-3); font-size: .74rem; font-weight: 800; letter-spacing: .03em; text-transform: uppercase; }
    .ms-device-toggle { display: flex; gap: .3rem; padding: 3px; border-radius: 999px; background: var(--ts-scrollbar-track); }
    .ms-device-toggle button { display: inline-flex; align-items: center; gap: .3rem; padding: .35rem .7rem; border: 0; border-radius: 999px; color: var(--ts-text-sky-350-3); background: transparent; font: inherit; font-size: .68rem; font-weight: 700; text-transform: none; letter-spacing: 0; cursor: pointer; }
    .ms-device-toggle button.active { color: var(--ts-text-on-accent); background: var(--ts-primary); }
    .ms-preview-empty { display: grid; justify-items: center; gap: .5rem; padding: 2.5rem 1rem; color: var(--ts-text-sky-450-11); text-align: center; }
    .ms-preview-empty i { font-size: 1.8rem; }
    .ms-preview-empty p { margin: 0; max-width: 26ch; font-size: .78rem; }

    /* ---- Tarjeta de vista previa al compartir (WhatsApp/Facebook/etc.) ---- */
    .ms-share-card-wrap { display: grid; gap: .6rem; padding: .6rem 0 .2rem; }
    .ms-share-card { overflow: hidden; border: 1px solid var(--ts-border-strong); border-radius: 12px; background: var(--ts-scrollbar-track); }
    .ms-share-card-image { width: 100%; aspect-ratio: 1200 / 630; background-color: var(--ts-bg-navy-850-31); background-position: center; background-size: cover; background-repeat: no-repeat; }
    .ms-share-card-body { display: grid; gap: .25rem; padding: .7rem .85rem .9rem; }
    .ms-share-card-domain { color: var(--ts-text-sky-450-11); font-size: .66rem; font-weight: 700; letter-spacing: .03em; text-transform: uppercase; }
    .ms-share-card-title { color: var(--ts-text-blue-50-7); font-size: .88rem; font-weight: 800; line-height: 1.25; overflow: hidden; text-overflow: ellipsis; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
    .ms-share-card-desc { margin: 0; color: var(--ts-text-sky-350-3); font-size: .76rem; line-height: 1.35; overflow: hidden; text-overflow: ellipsis; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
    .ms-share-card-hint { display: flex; align-items: flex-start; gap: .35rem; }

    /* ---- Mockups de dispositivo ----
     * Ninguno de los dos "estira" el sitio a lo ancho del panel: el laptop
     * renderiza el iframe a un ancho de escritorio real (LAPTOP_IFRAME_WIDTH)
     * y lo reduce con transform:scale (factor calculado en TS vía
     * ResizeObserver) para que el layout que se ve sea el de escritorio de
     * verdad, no una versión angosta/móvil de la página metida en una caja
     * chica. El teléfono ya carga a un ancho móvil real (375px) y se reduce
     * como "mini teléfono" completo (chasis + pantalla) con zoom, para que
     * no ocupe tanto espacio vertical.
     *
     * El ALTO del iframe (LAPTOP_IFRAME_HEIGHT / PHONE_IFRAME_HEIGHT) es una
     * ventana fija -- como la de una laptop/teléfono reales -- y NO "todo el
     * contenido de la página". Se probó lo segundo (medir el alto real del
     * sitio y estirar el iframe para que quepa completo) y falló por dos
     * motivos: 1) el shell de la app usa min-height:100vh, así que agrandar
     * el iframe agranda ese mínimo, lo que vuelve a agrandar el alto medido
     * -- una retroalimentación que crece sin parar; 2) aun resuelto eso, un
     * div externo con scroll NUNCA recibe la rueda del mouse cuando el
     * cursor está sobre el iframe que contiene -- son contextos de
     * navegación separados en el navegador, no una limitación de CSS. La
     * solución real es dejar que el iframe scrollee su documento de forma
     * nativa (sin scrolling="no"), como cualquier página embebida. */
    .ms-mock-wrap { display: grid; place-items: center; padding: .4rem 0 .2rem; }

    .ms-mock-laptop { width: min(100%, 560px); }
    .ms-mock-screen { position: relative; padding: 14px 14px 8px; border-radius: 14px 14px 6px 6px; background: linear-gradient(180deg, var(--ts-bg-navy-800-7), var(--ts-bg-navy-900-9)); box-shadow: 0 18px 34px var(--ts-shadow-gray-1000-a38), inset 0 0 0 1px var(--ts-shadow-gray-0-a5); }
    .ms-mock-cam { position: absolute; top: 5px; left: 50%; width: 5px; height: 5px; border-radius: 50%; background: var(--ts-bg-navy-700-3); transform: translateX(-50%); }
    /* No scrollea -- coincide exactamente con el tamaño de .ms-mock-scaled,
     * solo redondea la esquina. El scroll real pasa DENTRO del iframe (ver
     * nota junto a LAPTOP_IFRAME_HEIGHT arriba): un iframe encima de un
     * contenedor con su propio scroll nunca deja pasar la rueda del mouse
     * al contenedor -- son contextos de navegación separados. */
    .ms-mock-viewport { width: 100%; overflow: hidden; border-radius: 4px; background: var(--ts-bg-gray-0); }
    .ms-mock-scaled { overflow: hidden; }
    .ms-mock-scaled iframe { display: block; border: 0; background: var(--ts-bg-gray-0); transform-origin: top left; }
    .ms-mock-base { height: 14px; margin: 0 -8px; border-radius: 0 0 10px 10px; background: linear-gradient(180deg, var(--ts-bg-navy-750-3), var(--ts-bg-navy-900-10)); box-shadow: 0 10px 18px var(--ts-shadow-gray-1000-a30); display: flex; justify-content: center; }
    .ms-mock-hinge { width: 70px; height: 5px; margin-top: 3px; border-radius: 0 0 6px 6px; background: var(--ts-bg-navy-900-9); }

    /* "Mini teléfono": el chasis completo (notch + pantalla + barra inferior)
     * se reduce como una sola unidad -- zoom (a diferencia de transform)
     * también reduce el espacio que ocupa en el layout, así que no hace
     * falta un contenedor aparte con tamaño recalculado a mano. La pantalla
     * interna (375×760, antes del zoom) usa una proporción de teléfono real
     * (~1:2), no la caja casi cuadrada de antes -- por eso el resultado se
     * veía "cuadrado" en vez de un teléfono en miniatura. */
    .ms-mock-phone { position: relative; width: 100%; max-width: 391px; padding: 14px 8px 16px; border-radius: 36px; background: linear-gradient(180deg, var(--ts-bg-navy-800-7), var(--ts-bg-navy-900-9)); box-shadow: 0 18px 34px var(--ts-shadow-gray-1000-a38), inset 0 0 0 1px var(--ts-shadow-gray-0-a6); box-sizing: border-box; zoom: .58; }
    .ms-mock-notch { position: absolute; top: 12px; left: 50%; width: 62px; height: 15px; border-radius: 10px; background: var(--ts-bg-navy-900-9); transform: translateX(-50%); z-index: 2; }
    .ms-mock-phone-screen { width: 100%; height: 760px; overflow: hidden; border-radius: 22px; background: var(--ts-bg-gray-0); }
    /* Igual que en el laptop: alto generoso para que el documento real
     * nunca active su PROPIO scroll interno -- solo debe scrollear
     * .ms-mock-phone-screen (ver nota sobre scrolling="no" más arriba). */
    /* El alto real lo fija [style.height.px]="phoneContentHeight()" en el
     * template (reportado por el sitio adentro, ver PREVIEW_HEIGHT_MESSAGE_TYPE);
     * aquí solo el ancho, que sí es fijo (viewport móvil real). */
    .ms-mock-phone-screen iframe { display: block; width: 375px; max-width: 100%; border: 0; background: var(--ts-bg-gray-0); }
    .ms-mock-home-bar { display: block; width: 64px; height: 4px; margin: 8px auto 0; border-radius: 4px; background: var(--ts-bg-navy-700-3); }

    @media (max-width: 1180px) {
      /* minmax(0, 1fr), no un "1fr" a secas: un grid-item nunca se encoge
       * por debajo del tamaño de SU CONTENIDO (acá, el mockup de laptop,
       * con un ancho propio de hasta 560px) salvo que su pista tenga un
       * mínimo explícito de 0 -- sin esto, en pantallas angostas toda la
       * columna se quedaba forzada a ~560px+ de ancho aunque la pantalla
       * fuera de 390px, empujando scroll horizontal a toda la página. */
      .ms-columns { grid-template-columns: minmax(0, 1fr); }
      .ms-preview { position: static; }
    }
    /* Tablet y móvil: 2 filas fijas (3 pestañas por fila, son 6 en total) en
     * vez del scroll horizontal de escritorio -- así se ven las 6 de un
     * vistazo, sin tener que descubrir que hay más deslizando. */
    @media (max-width: 900px) {
      /* minmax(0, 1fr), no "1fr" a secas -- mismo motivo que .ms-columns
       * más arriba: sin el mínimo en 0, una pestaña larga ("Colores y
       * estilo", "Redes sociales") empuja la fila entera más ancha que la
       * pantalla. white-space normal (en vez de nowrap) deja que esas
       * etiquetas envuelvan a dos líneas dentro de su celda angosta en vez
       * de desbordar o cortarse. */
      .ms-subnav { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: .35rem; overflow-x: visible; }
      .ms-subnav-item { flex-direction: column; justify-content: center; gap: .25rem; min-height: 52px; padding: .35rem .3rem; white-space: normal; text-align: center; font-size: .68rem; line-height: 1.15; }
    }
    @media (max-width: 640px) {
      .ms-header-actions { width: 100%; }
      .ms-btn { flex: 1 1 auto; justify-content: center; }
      .ms-field-row { flex-direction: column; align-items: stretch; }
      .ms-color-grid { grid-template-columns: minmax(0, 1fr); }
    }
  `]
})
export class MiSitioComponent implements OnInit, AfterViewChecked, OnDestroy {
  readonly sections: { id: MiSitioSection; label: string; icon: string }[] = [
    { id: 'general', label: 'General', icon: 'bi bi-house' },
    { id: 'portada', label: 'Portada', icon: 'bi bi-image' },
    { id: 'colores', label: 'Colores y estilo', icon: 'bi bi-palette' },
    { id: 'informacion', label: 'Información', icon: 'bi bi-info-circle' },
    { id: 'secciones', label: 'Secciones', icon: 'bi bi-list-check' },
    { id: 'redes', label: 'Redes sociales', icon: 'bi bi-share' },
    { id: 'vista-enlace', label: 'Vista de enlace', icon: 'bi bi-link-45deg' },
    { id: 'privado', label: 'Acceso privado', icon: 'bi bi-shield-lock' },
  ];
  readonly sectionToggles: { key: keyof VisibleSections; label: string; hint: string; icon: string }[] = [
    { key: 'spaces', label: 'Espacios', hint: 'La grilla con todos tus espacios publicados.', icon: 'bi bi-door-open' },
    { key: 'featured', label: 'Espacios destacados', hint: 'El carrusel de espacios destacados, si tu plan lo permite.', icon: 'bi bi-gem' },
    { key: 'about', label: 'Sobre nosotros', hint: 'El texto de presentación de tu negocio.', icon: 'bi bi-building' },
    { key: 'contact', label: 'Contacto', hint: 'Dirección, teléfono, correo y redes sociales.', icon: 'bi bi-telephone' },
  ];

  activeSection = signal<MiSitioSection>('general');
  previewDevice = signal<PreviewDevice>('desktop');

  mySite = signal<BusinessSiteInfo | null>(null);
  mySiteLoading = signal(false);
  mySiteError = signal('');
  canCustomizeSite = computed(() => !!this.mySite()?.canCustomizeSite);
  canCustomizeSlug = computed(() => !!this.mySite()?.canCustomizeSlug);

  businessNameDraft = signal('');
  selectedLogo = signal<File | null>(null);
  logoPreview = signal<string | null>(null);
  isUploadingLogo = signal(false);

  slugDraft = signal('');

  heroTitleDraft = signal('');
  heroSubtitleDraft = signal('');
  ctaTextDraft = signal('');
  selectedBanner = signal<File | null>(null);
  bannerPreview = signal<string | null>(null);

  // --- Vista previa al compartir el enlace (tarjeta de WhatsApp/Facebook/etc.) ---
  shareTitleDraft = signal('');
  shareDescriptionDraft = signal('');
  selectedShareImage = signal<File | null>(null);
  shareImagePreview = signal<string | null>(null);

  // --- Acceso privado (código de 4 dígitos) ---
  canUsePrivateAccess = computed(() => !!this.mySite()?.canUsePrivateAccess);
  privacyInfo = signal<BusinessPrivacyInfo | null>(null);
  isTogglingPrivacy = signal(false);
  isRegeneratingCode = signal(false);
  accessWelcomeTitleDraft = signal('');
  accessWelcomeMessageDraft = signal('');
  isSharingAccessLink = signal(false);
  isCopyingAccessLink = signal(false);
  showInvitationEditor = signal(false);
  /** Espacios del negocio para el selector "Aplica a" del editor de imagen
   * (misma plantilla, apuntando al enlace de un espacio puntual en vez del
   * sitio general). */
  privateAccessSpaces = signal<{ id: number; title: string; url: string; isPrivate: boolean }[]>([]);
  /** Cuántos espacios marcó privados el dueño (solo se muestra en su panel,
   * nunca en el sitio público). */
  privateSpacesCount = computed(() => this.privateAccessSpaces().filter(space => space.isPrivate).length);
  initialInvitationSpaceId = signal<number | null>(null);
  /** Espacio pedido por la URL (botón "Crear imagen para imprimir" de una
   * tarjeta puntual en "Gestión de salas", ver dashboard.component.ts
   * goToCreatePrintImage) -- se consume una sola vez, en cuanto el código de
   * acceso ya está cargado, para poder abrir el editor con ese espacio
   * preseleccionado. */
  private pendingInvitationSpaceId = signal<number | null>(null);

  primaryColorDraft = signal(DEFAULT_PRIMARY_COLOR);
  secondaryColorDraft = signal(DEFAULT_SECONDARY_COLOR);
  backgroundColorDraft = signal(DEFAULT_BACKGROUND_COLOR);
  navbarColorDraft = signal(DEFAULT_NAVBAR_COLOR);
  highlightTextColorDraft = signal(DEFAULT_HIGHLIGHT_TEXT_COLOR);

  shortDescriptionDraft = signal('');
  aboutTextDraft = signal('');
  addressDraft = signal('');
  phoneDraft = signal('');
  emailDraft = signal('');
  isSavingEmail = signal(false);
  emailChanged = computed(() => this.emailDraft().trim().toLowerCase() !== (this.mySite()?.email || '').trim().toLowerCase());

  visibleSectionsDraft = signal<VisibleSections>({ ...DEFAULT_VISIBLE_SECTIONS });

  /** Borrador de redes sociales (lista ordenada) -- ver app-platform-social-links-admin en modo business. */
  socialLinksDraft = signal<BusinessSocialLink[]>([]);
  /** Error de validación/servidor de una red puntual, para resaltar su fila. */
  socialLinksError = signal<{ index: number | null; message: string } | null>(null);

  isSavingDraft = signal(false);
  isPublishing = signal(false);

  /** Snapshot de lo último guardado -- contra esto se compara cada draft
   * para saber si hay cambios sin guardar (ver hasUnsavedChanges más abajo
   * y MiSitioUnsavedChangesService, consultado por el dashboard antes de
   * dejar salir de esta pestaña). */
  private lastSavedSnapshot = signal<BusinessSiteInfo | null>(null);
  private lastSavedName = signal('');

  hasUnsavedChanges = computed(() => {
    const snap = this.lastSavedSnapshot();
    if (!snap) return false;
    return (
      this.businessNameDraft().trim() !== this.lastSavedName().trim() ||
      this.slugDraft().trim() !== (snap.slug || '') ||
      this.heroTitleDraft().trim() !== (snap.heroTitle || '') ||
      this.heroSubtitleDraft().trim() !== (snap.heroSubtitle || '') ||
      this.ctaTextDraft().trim() !== (snap.ctaText || '') ||
      this.primaryColorDraft() !== (snap.primaryColor || DEFAULT_PRIMARY_COLOR) ||
      this.secondaryColorDraft() !== (snap.secondaryColor || DEFAULT_SECONDARY_COLOR) ||
      this.backgroundColorDraft() !== (snap.backgroundColor || DEFAULT_BACKGROUND_COLOR) ||
      this.navbarColorDraft() !== (snap.navbarColor || DEFAULT_NAVBAR_COLOR) ||
      this.highlightTextColorDraft() !== (snap.highlightTextColor || DEFAULT_HIGHLIGHT_TEXT_COLOR) ||
      this.shortDescriptionDraft().trim() !== (snap.shortDescription || '') ||
      this.aboutTextDraft().trim() !== (snap.aboutText || '') ||
      this.addressDraft().trim() !== (snap.address || '') ||
      this.phoneDraft().trim() !== (snap.phone || '') ||
      this.emailChanged() ||
      serializeSocialLinks(this.socialLinksDraft()) !== serializeSocialLinks(snap.socialLinks || []) ||
      JSON.stringify(this.visibleSectionsDraft()) !== JSON.stringify(snap.visibleSections || DEFAULT_VISIBLE_SECTIONS) ||
      !!this.selectedBanner() ||
      this.shareTitleDraft().trim() !== (snap.shareTitle || '') ||
      this.shareDescriptionDraft().trim() !== (snap.shareDescription || '') ||
      !!this.selectedShareImage()
    );
  });

  readonly clientAppBaseUrl = environment.clientAppUrl;
  private readonly LOGOS_BASE_URL = 'https://storage.googleapis.com/thehubp/business-logos/';
  /** Dominio mostrado en la tarjeta de vista previa (WhatsApp/Facebook lo
   * muestran junto al título) -- mismo host que clientAppBaseUrl, sin protocolo. */
  readonly shareCardDomain = this.clientAppBaseUrl.replace(/^https?:\/\//, '');
  readonly shareCardFallbackImage = `${this.clientAppBaseUrl}/assets/img/tusala-og.jpg`;
  /** Bug real corregido: esta vista previa siempre caía a la imagen genérica
   * de TuSala, sin importar si el negocio es privado -- mismo criterio que
   * getSitePreview en el backend (real vista al compartir): si el dueño no
   * subió su propia imagen, un negocio privado usa esta ("enlace-privado"),
   * nunca la genérica, para que quien reciba el enlace entienda de una que
   * es un acceso restringido. */
  readonly privateShareCardImage = `${this.clientAppBaseUrl}/assets/img/enlace-privado-og.jpg`;

  usingPrivateFallbackImage(site: BusinessSiteInfo): boolean {
    return !this.shareImagePreview() && !site.shareImageUrl && !!this.privacyInfo()?.isPrivate;
  }

  // --- Mockup de laptop: el iframe siempre carga a LAPTOP_IFRAME_WIDTH (ancho
  // de escritorio real) y se reduce visualmente con transform:scale para que
  // el layout mostrado sea el de escritorio -- nunca el móvil que el sitio
  // mostraría si el iframe fuera literalmente angosto. El factor de escala se
  // recalcula con un ResizeObserver sobre el panel de vista previa, para que
  // siga siendo fiel al espacio real disponible en cualquier ancho de pantalla.
  @ViewChild('previewPanel') private previewPanelRef?: ElementRef<HTMLDivElement>;
  @ViewChild('previewIframe') private previewIframeRef?: ElementRef<HTMLIFrameElement>;
  readonly laptopIframeWidth = LAPTOP_IFRAME_WIDTH;
  readonly laptopIframeHeight = LAPTOP_IFRAME_HEIGHT;
  readonly phoneIframeHeight = PHONE_IFRAME_HEIGHT;
  laptopScale = signal(LAPTOP_SHELL_MAX_WIDTH / LAPTOP_IFRAME_WIDTH);
  laptopScaledWidth = computed(() => Math.round(LAPTOP_IFRAME_WIDTH * this.laptopScale()));
  laptopScaledHeight = computed(() => Math.round(LAPTOP_IFRAME_HEIGHT * this.laptopScale()));
  private laptopResizeObserver: ResizeObserver | null = null;
  private laptopObserverAttached = false;

  mySiteUrl = computed(() => {
    const slug = this.mySite()?.slug;
    return slug ? `${this.clientAppBaseUrl}/${slug}` : null;
  });
  isCopyingSiteUrl = signal(false);
  isSharingSiteUrl = signal(false);
  previewUrl = computed(() => {
    const site = this.mySite();
    if (!site?.slug || !site?.previewToken) return null;
    return `${this.clientAppBaseUrl}/${site.slug}?preview_token=${encodeURIComponent(site.previewToken)}`;
  });
  safePreviewUrl = computed<SafeResourceUrl | null>(() => {
    const url = this.previewUrl();
    return url ? this.sanitizer.bypassSecurityTrustResourceUrl(url) : null;
  });
  lastUpdatedLabel = computed(() => {
    const updatedAt = this.mySite()?.updatedAt;
    if (!updatedAt) return '';
    return new Intl.DateTimeFormat('es-SV', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(updatedAt));
  });

  get currentBusiness() {
    return this.authService.currentUser;
  }

  constructor(
    private authService: AuthService,
    private businessConfigService: BusinessConfigService,
    private dialogs: DialogService,
    private notifications: NotificationService,
    public salaService: SalaService,
    private sanitizer: DomSanitizer,
    private router: Router,
    private route: ActivatedRoute,
    private unsavedChanges: MiSitioUnsavedChangesService,
  ) {
    // Mantiene al servicio compartido (consultado por el dashboard antes de
    // dejar esta pestaña) sincronizado con el estado real del formulario.
    effect(() => {
      this.unsavedChanges.hasUnsavedChanges.set(this.hasUnsavedChanges());
    });
    // Abre el editor de imagen con el espacio de la URL preseleccionado
    // (botón "Crear imagen para imprimir" de Gestión de salas) en cuanto el
    // código de acceso ya esté disponible -- antes de eso el editor no
    // puede abrirse (necesita privacyInfo()?.accessCode).
    effect(() => {
      const pendingId = this.pendingInvitationSpaceId();
      if (pendingId !== null && this.privacyInfo()?.accessCode) {
        this.openInvitationEditor(pendingId);
        this.pendingInvitationSpaceId.set(null);
      }
    });
    // Vista previa en vivo: cada cambio en cualquier campo reenvía el
    // borrador completo al iframe por postMessage, sin depender de "Guardar
    // borrador" -- ver GalleryComponent (front público) para el receptor.
    effect(() => {
      const payload = this.buildDraftPayload();
      this.broadcastDraftToPreview(payload);
    });
  }

  ngOnInit(): void {
    this.businessNameDraft.set(this.currentBusiness()?.name || '');
    this.lastSavedName.set(this.currentBusiness()?.name || '');
    this.loadMySite();
    if (!this.salaService.espacios().length && !this.salaService.espaciosLoading()) this.salaService.loadEspacios();
    // Deep link desde "Gestión de salas" (goToCreatePrintImage): ?section=privado&space=ID.
    const params = this.route.snapshot.queryParamMap;
    if (params.get('section') === 'privado') this.activeSection.set('privado');
    const spaceParam = params.get('space');
    if (spaceParam && /^\d+$/.test(spaceParam)) this.pendingInvitationSpaceId.set(Number(spaceParam));
  }

  // El panel #previewPanel solo existe en el DOM una vez cargado el sitio
  // (detrás de varios *ngIf) -- se reintenta en cada ciclo de detección de
  // cambios hasta que aparezca, y ahí se conecta el observer una sola vez.
  ngAfterViewChecked(): void {
    if (this.laptopObserverAttached || !this.previewPanelRef) return;
    this.laptopObserverAttached = true;
    this.laptopResizeObserver = new ResizeObserver(entries => {
      const width = entries[0]?.contentRect.width;
      if (!width) return;
      // .ms-mock-laptop se limita en CSS a min(100%, 560px) -- hay que topar
      // el mismo valor aquí o el ancho "real" calculado (basado en un panel
      // más ancho que eso) desbordaría el bisel, que sí quedó topado a 560px.
      const usable = Math.max(1, Math.min(width, LAPTOP_SHELL_MAX_WIDTH) - LAPTOP_SHELL_CHROME);
      this.laptopScale.set(usable / LAPTOP_IFRAME_WIDTH);
    });
    this.laptopResizeObserver.observe(this.previewPanelRef.nativeElement);
  }

  ngOnDestroy(): void {
    this.laptopResizeObserver?.disconnect();
    // Este componente ya no existe para mantenerlo al día -- si quedara en
    // true, bloquearía salidas futuras de otra pestaña sin motivo.
    this.unsavedChanges.hasUnsavedChanges.set(false);
  }

  selectSection(section: MiSitioSection): void {
    this.activeSection.set(section);
  }

  goToSubscription(): void {
    this.router.navigate([], { queryParams: { module: 'suscripcion' }, queryParamsHandling: 'merge' });
  }

  async showUpgradePrompt(): Promise<void> {
    const upgrade = await this.dialogs.confirm({
      title: 'Personaliza tu sitio web',
      message: 'Tu plan actual no incluye la personalización de tu sitio público (portada, colores, textos, secciones, redes y contacto). Escala de plan para desbloquear este beneficio y darle una imagen propia a tu negocio.',
      tone: 'info',
      confirmText: 'Ver planes',
      cancelText: 'Ahora no',
    });
    if (upgrade) this.goToSubscription();
  }

  verSitio(): void {
    const url = this.mySiteUrl();
    if (url) window.open(url, '_blank', 'noopener');
  }

  // Corrección pedida: el enlace del sitio solo se podía copiar/compartir
  // desde el accesorio del dashboard (shareMySiteWebsite, que además pide
  // el sitio de nuevo por API) -- aquí, en la propia pantalla de "Mi
  // sitio", el enlace ya está cargado en mySiteUrl(), así que no hace
  // falta ninguna petición adicional.
  async copyMySiteUrl(): Promise<void> {
    const url = this.mySiteUrl();
    if (!url || this.isCopyingSiteUrl()) return;
    this.isCopyingSiteUrl.set(true);
    try {
      await navigator.clipboard.writeText(url);
      this.notifications.success('Enlace de tu sitio copiado al portapapeles.');
    } catch {
      this.notifications.error(`No fue posible copiar automáticamente. Enlace: ${url}`);
    } finally {
      this.isCopyingSiteUrl.set(false);
    }
  }

  // Mismo patrón que shareMySiteWebsite() en dashboard.component.ts: hoja
  // de compartir nativa del dispositivo si está disponible, y si no (la
  // mayoría de navegadores de escritorio), cae a copiar al portapapeles.
  async shareMySiteUrl(): Promise<void> {
    const url = this.mySiteUrl();
    if (!url || this.isSharingSiteUrl()) return;
    this.isSharingSiteUrl.set(true);
    try {
      if (navigator.share) {
        try {
          await navigator.share({ title: this.businessNameDraft().trim() || 'Mi sitio', url });
          return;
        } catch (shareError: any) {
          // AbortError = la persona cerró la hoja de compartir sin elegir
          // nada -- no es un error, no hace falta caer al portapapeles.
          if (shareError?.name === 'AbortError') return;
        }
      }
      await navigator.clipboard.writeText(url);
      this.notifications.success('Enlace de tu sitio copiado al portapapeles.');
    } catch {
      this.notifications.error(`No fue posible compartir ni copiar automáticamente. Enlace: ${url}`);
    } finally {
      this.isSharingSiteUrl.set(false);
    }
  }

  loadMySite(): void {
    const businessId = this.currentBusiness()?.id;
    if (!businessId) return;
    this.mySiteLoading.set(true);
    this.mySiteError.set('');
    this.businessConfigService.obtenerSitio(businessId).subscribe({
      next: site => {
        this.applySiteToDrafts(site);
        this.mySiteLoading.set(false);
        if (site.canUsePrivateAccess) { this.loadPrivacyInfo(); this.loadPrivateAccessSpaces(); }
      },
      error: err => {
        this.mySiteLoading.set(false);
        this.mySiteError.set(typeof err === 'string' ? err : 'No fue posible cargar tu sitio.');
      },
    });
  }

  private applySiteToDrafts(site: BusinessSiteInfo): void {
    this.mySite.set(site);
    this.lastSavedSnapshot.set(site);
    this.lastSavedName.set(this.currentBusiness()?.name || '');
    this.slugDraft.set(site.slug || '');
    this.heroTitleDraft.set(site.heroTitle || '');
    this.heroSubtitleDraft.set(site.heroSubtitle || '');
    this.ctaTextDraft.set(site.ctaText || '');
    this.primaryColorDraft.set(site.primaryColor || DEFAULT_PRIMARY_COLOR);
    this.secondaryColorDraft.set(site.secondaryColor || DEFAULT_SECONDARY_COLOR);
    this.backgroundColorDraft.set(site.backgroundColor || DEFAULT_BACKGROUND_COLOR);
    this.navbarColorDraft.set(site.navbarColor || DEFAULT_NAVBAR_COLOR);
    this.highlightTextColorDraft.set(site.highlightTextColor || DEFAULT_HIGHLIGHT_TEXT_COLOR);
    this.shortDescriptionDraft.set(site.shortDescription || '');
    this.aboutTextDraft.set(site.aboutText || '');
    this.addressDraft.set(site.address || '');
    this.phoneDraft.set(site.phone || '');
    this.emailDraft.set(site.email || '');
    this.socialLinksDraft.set((site.socialLinks || []).map(link => ({ ...link })));
    this.socialLinksError.set(null);
    this.visibleSectionsDraft.set(site.visibleSections || { ...DEFAULT_VISIBLE_SECTIONS });
    this.shareTitleDraft.set(site.shareTitle || '');
    this.shareDescriptionDraft.set(site.shareDescription || '');
    this.accessWelcomeTitleDraft.set(site.accessWelcomeTitle || '');
    this.accessWelcomeMessageDraft.set(site.accessWelcomeMessage || '');
  }

  loadPrivacyInfo(): void {
    const businessId = this.currentBusiness()?.id;
    if (!businessId) return;
    this.businessConfigService.obtenerPrivacidad(businessId).subscribe({
      next: info => this.privacyInfo.set(info),
      error: () => {},
    });
  }

  /** Lista completa de espacios (no la paginada de 12 que ya carga
   * ngOnInit) para que el selector "Aplica a" del editor de imagen nunca
   * omita espacios en negocios con muchos. */
  loadPrivateAccessSpaces(): void {
    this.salaService.obtenerEspaciosParaSelector().subscribe({
      next: spaces => this.privateAccessSpaces.set(
        spaces
          .filter(space => space.is_active !== false)
          .map(space => ({ id: space.space_id, title: space.title, url: `${this.clientAppBaseUrl}/espacio/${space.space_id}`, isPrivate: !!space.is_private })),
      ),
      error: () => {},
    });
  }

  goToSpaces(): void {
    this.router.navigate([], { queryParams: { module: 'salas' }, queryParamsHandling: 'merge' });
  }

  openInvitationEditor(spaceId: number | null): void {
    this.initialInvitationSpaceId.set(spaceId);
    this.showInvitationEditor.set(true);
  }

  async togglePrivacy(next: boolean): Promise<void> {
    const businessId = this.currentBusiness()?.id;
    if (!businessId || this.isTogglingPrivacy()) return;
    if (!next) {
      const confirmed = await this.dialogs.confirm({
        title: 'Hacer público el negocio',
        message: 'Tus espacios volverán a verse en la galería pública sin necesidad de código, excepto los que privatizaste uno por uno en "Gestión de salas" (esos siguen pidiendo el código). El código de acceso se conserva por si quieres volver a activarlo más adelante.',
        tone: 'warning',
        confirmText: 'Hacer público',
        cancelText: 'Cancelar',
      });
      if (!confirmed) return;
    }
    this.isTogglingPrivacy.set(true);
    try {
      const info = await firstValueFrom(this.businessConfigService.actualizarPrivacidad(businessId, { is_private: next }));
      this.privacyInfo.set(info);
      this.notifications.success(next ? 'Tu negocio ahora es privado.' : 'Tu negocio volvió a ser público.');
    } catch (error: any) {
      this.notifications.error(friendlyError(error, 'No fue posible actualizar la privacidad.'));
    } finally {
      this.isTogglingPrivacy.set(false);
    }
  }

  async regenerateAccessCode(): Promise<void> {
    const businessId = this.currentBusiness()?.id;
    if (!businessId || this.isRegeneratingCode()) return;
    const confirmed = await this.dialogs.confirm({
      title: 'Generar un nuevo código',
      message: 'El código actual dejará de funcionar de inmediato, y quienes ya habían entrado con él (incluidos clientes con sesión iniciada) tendrán que ingresar el nuevo. Tendrás que compartir el nuevo código con tus clientes por tu cuenta.',
      tone: 'warning',
      confirmText: 'Generar nuevo código',
      cancelText: 'Cancelar',
    });
    if (!confirmed) return;
    this.isRegeneratingCode.set(true);
    try {
      const info = await firstValueFrom(this.businessConfigService.actualizarPrivacidad(businessId, { regenerate_code: true }));
      this.privacyInfo.set(info);
      this.notifications.success('Se generó un nuevo código de acceso.');
    } catch (error: any) {
      this.notifications.error(friendlyError(error, 'No fue posible generar un nuevo código.'));
    } finally {
      this.isRegeneratingCode.set(false);
    }
  }

  /** Copia SIEMPRE al portapapeles (nunca abre la hoja de compartir) --
   * botón independiente de shareAccessLink(), mismo criterio que
   * copyMySiteUrl()/shareMySiteUrl() de "Vista de enlace" de arriba: ambas
   * opciones conviven, una no reemplaza a la otra. */
  async copyAccessLink(): Promise<void> {
    const info = this.privacyInfo();
    const url = info?.url || this.mySiteUrl();
    if (!url || !info?.accessCode || this.isCopyingAccessLink()) return;
    const businessName = this.currentBusiness()?.name || 'nuestro espacio';
    this.isCopyingAccessLink.set(true);
    try {
      // Enlace en la primera línea: si se pega en la barra del navegador abre
      // el sitio (y el front quita el código de la URL); con el texto primero
      // se convertía en una búsqueda web que incluía el código.
      await navigator.clipboard.writeText(`${url}\nCódigo de acceso: ${info.accessCode}\nAcceso privado a ${businessName} en TuSala.`);
      this.notifications.success('Enlace y código copiados al portapapeles.');
    } catch {
      this.notifications.error(`No fue posible copiar automáticamente. Enlace: ${url} · Código: ${info.accessCode}`);
    } finally {
      this.isCopyingAccessLink.set(false);
    }
  }

  /** Mismo patrón que shareMySiteUrl(): hoja de compartir nativa si está
   * disponible (el código va en `text`, aparte de `url` -- WhatsApp y la
   * mayoría de apps muestran ambos juntos), y si no, copia al portapapeles
   * la URL y el código como mensaje acompañante -- nunca embebido en la
   * URL misma. */
  async shareAccessLink(): Promise<void> {
    const info = this.privacyInfo();
    const url = info?.url || this.mySiteUrl();
    if (!url || !info?.accessCode || this.isSharingAccessLink()) return;
    const businessName = this.currentBusiness()?.name || 'nuestro espacio';
    const text = `Acceso privado a ${businessName} en TuSala. Código de acceso: ${info.accessCode}`;
    this.isSharingAccessLink.set(true);
    try {
      if (navigator.share) {
        try {
          await navigator.share({ title: businessName, text, url });
          return;
        } catch (shareError: any) {
          if (shareError?.name === 'AbortError') return;
        }
      }
      await navigator.clipboard.writeText(`${text}\n${url}`);
      this.notifications.success('Enlace y código copiados al portapapeles.');
    } catch {
      this.notifications.error(`No fue posible compartir ni copiar automáticamente. Enlace: ${url} · Código: ${info.accessCode}`);
    } finally {
      this.isSharingAccessLink.set(false);
    }
  }

  /** QR simple (sin logo ni superposición) que apunta a la pantalla de
   * acceso -- para imprimir en un mural o volante físico. Se genera del
   * lado del navegador, sin depender del servidor. */
  async downloadAccessQr(): Promise<void> {
    const url = this.privacyInfo()?.url || this.mySiteUrl();
    if (!url) return;
    try {
      const dataUrl = await QRCode.toDataURL(url, { width: 640, margin: 2 });
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `codigo-qr-${this.mySite()?.slug || 'acceso'}.png`;
      link.click();
    } catch {
      this.notifications.error('No fue posible generar el código QR.');
    }
  }

  // --- Nombre del negocio: se guarda solo al perder el foco (sin botón),
  // disponible en cualquier plan -- no es "personalización del sitio", es
  // identidad básica del negocio, igual que en Configuración. ---
  async guardarNombreSiCambio(): Promise<void> {
    const businessId = this.currentBusiness()?.id;
    const name = this.businessNameDraft().trim();
    if (!businessId || !name || name === this.lastSavedName().trim()) return;
    try {
      const response = await firstValueFrom(this.businessConfigService.actualizarPerfil(businessId, { name }));
      this.authService.updateCurrentUser(response.business);
      this.lastSavedName.set(name);
      this.notifications.success('Nombre actualizado.');
    } catch (error: any) {
      this.businessNameDraft.set(this.lastSavedName());
      this.notifications.error(friendlyError(error, 'No fue posible actualizar el nombre.'));
    }
  }

  // --- Logo: se sube apenas se selecciona (con un modal de confirmación,
  // sin un botón "Guardar" aparte) -- también disponible en cualquier plan. ---
  getLogoUrl(logo: string | null | undefined): string {
    if (!logo) return '';
    return `${this.LOGOS_BASE_URL}${logo}`;
  }

  async onLogoSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    const file = input.files[0];
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      this.notifications.error('Formato de archivo no válido. Use PNG, JPG, JPEG, WEBP o GIF');
      input.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      this.notifications.error('El archivo es demasiado grande. Máximo 5MB');
      input.value = '';
      return;
    }
    this.selectedLogo.set(file);
    const reader = new FileReader();
    reader.onload = (e) => this.logoPreview.set(e.target?.result as string);
    reader.readAsDataURL(file);

    const businessId = this.currentBusiness()?.id;
    if (!businessId) return;
    this.isUploadingLogo.set(true);
    try {
      const response = await this.dialogs.run(
        'Subiendo logo…',
        () => this.businessConfigService.actualizarLogo(businessId, file),
        'Logo actualizado exitosamente',
        (error: any) => friendlyError(error, 'Error al subir el logo'),
      );
      if (response !== null) {
        this.authService.updateCurrentUser(response.business);
        window.dispatchEvent(new CustomEvent('tusala:business-configuration-changed'));
      } else {
        this.logoPreview.set(null);
      }
    } catch (error) {
      console.error('Error al subir logo:', error);
      this.logoPreview.set(null);
    } finally {
      this.selectedLogo.set(null);
      this.isUploadingLogo.set(false);
      input.value = '';
    }
  }

  // --- Correo de contacto: campo con confirmación de contraseña (a
  // diferencia del resto de "Mi sitio", no se puede guardar en silencio). ---
  async guardarCorreoContacto(): Promise<void> {
    const businessId = this.currentBusiness()?.id;
    const newEmail = this.emailDraft().trim();
    if (!businessId || !newEmail || this.isSavingEmail()) return;
    this.isSavingEmail.set(true);
    try {
      const result = await this.dialogs.confirmAndRun(
        {
          title: 'Confirma tu contraseña',
          message: `Vas a cambiar el correo de contacto a ${newEmail}. Ingresa tu contraseña actual para confirmarlo.`,
          tone: 'warning',
          confirmText: 'Confirmar correo',
          fields: [{ name: 'current_password', label: 'Contraseña actual', type: 'password', required: true, placeholder: 'Tu contraseña de inicio de sesión' }],
        },
        (values: Record<string, string | boolean>) => this.businessConfigService.actualizarPersonalizacionSitio(businessId, {
          email: newEmail,
          currentPassword: String(values['current_password'] || ''),
        }),
        'Correo de contacto actualizado.',
        (error: any) => friendlyError(error, 'No fue posible cambiar el correo.'),
      );
      if (result !== null) this.applySiteToDrafts(result as BusinessSiteInfo);
    } finally {
      this.isSavingEmail.set(false);
    }
  }

  // --- Banner (queda pendiente de "Guardar borrador"/"Publicar cambios") ---
  onBannerSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    const file = input.files[0];
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      this.notifications.error('Formato de archivo no válido. Use PNG, JPG, JPEG, WEBP o GIF');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      this.notifications.error('El archivo es demasiado grande. Máximo 5MB');
      return;
    }
    this.selectedBanner.set(file);
    const reader = new FileReader();
    reader.onload = (e) => this.bannerPreview.set(e.target?.result as string);
    reader.readAsDataURL(file);
  }

  cancelarBanner(): void {
    this.selectedBanner.set(null);
    this.bannerPreview.set(null);
    const input = document.getElementById('miSitioBannerInput') as HTMLInputElement | null;
    if (input) input.value = '';
  }

  onShareImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    const file = input.files[0];
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      this.notifications.error('Formato de archivo no válido. Use PNG, JPG, JPEG, WEBP o GIF');
      return;
    }
    if (file.size > 30 * 1024 * 1024) {
      this.notifications.error('El archivo es demasiado grande. Máximo 30MB');
      return;
    }
    this.selectedShareImage.set(file);
    // Vista previa instantánea con el archivo local -- el recorte real a
    // 1200x630 lo hace el backend al guardar, esto es solo para que la
    // tarjeta de la derecha se actualice de inmediato sin esperar al servidor.
    const reader = new FileReader();
    reader.onload = (e) => this.shareImagePreview.set(e.target?.result as string);
    reader.readAsDataURL(file);
  }

  cancelarShareImage(): void {
    this.selectedShareImage.set(null);
    this.shareImagePreview.set(null);
    const input = document.getElementById('miSitioShareImageInput') as HTMLInputElement | null;
    if (input) input.value = '';
  }

  restablecerColores(): void {
    this.primaryColorDraft.set(DEFAULT_PRIMARY_COLOR);
    this.secondaryColorDraft.set(DEFAULT_SECONDARY_COLOR);
    this.backgroundColorDraft.set(DEFAULT_BACKGROUND_COLOR);
    this.navbarColorDraft.set(DEFAULT_NAVBAR_COLOR);
    this.highlightTextColorDraft.set(DEFAULT_HIGHLIGHT_TEXT_COLOR);
  }

  toggleSection(key: keyof VisibleSections): void {
    if (!this.canCustomizeSite()) return;
    this.visibleSectionsDraft.update(current => ({ ...current, [key]: !current[key] }));
  }

  // --- Vista previa en vivo (postMessage) ---
  onPreviewIframeLoad(): void {
    this.broadcastDraftToPreview(this.buildDraftPayload());
  }

  private buildDraftPayload(): MiSitioDraftPayload {
    return {
      businessName: this.businessNameDraft().trim(),
      bannerUrl: this.bannerPreview() || this.mySite()?.bannerUrl || null,
      primaryColor: this.primaryColorDraft(),
      secondaryColor: this.secondaryColorDraft(),
      backgroundColor: this.backgroundColorDraft(),
      navbarColor: this.navbarColorDraft(),
      highlightTextColor: this.highlightTextColorDraft(),
      heroTitle: this.heroTitleDraft().trim(),
      heroSubtitle: this.heroSubtitleDraft().trim(),
      ctaText: this.ctaTextDraft().trim(),
      shortDescription: this.shortDescriptionDraft().trim(),
      aboutText: this.aboutTextDraft().trim(),
      address: this.addressDraft().trim(),
      socialLinks: this.previewSocialLinks(),
      visibleSections: this.visibleSectionsDraft(),
    };
  }

  onSocialLinksDraftChange(links: BusinessSocialLink[]): void {
    this.socialLinksDraft.set(links);
    if (this.socialLinksError()) this.socialLinksError.set(null);
  }

  private socialNetwork(key: string): SocialNetworkOption {
    const catalog = this.mySite()?.socialCatalog || [];
    return catalog.find(n => n.key === key) || catalog.find(n => n.key === 'other')
      || { key: 'other', label: 'Otra red', icon: 'bi-link-45deg', color: '#6366F1', on_color: '#ffffff', placeholder: 'https://...' };
  }

  /** Redes visibles con enlace válido, como las verá el sitio (vista previa en vivo). */
  private previewSocialLinks(): MiSitioDraftPayload['socialLinks'] {
    return this.socialLinksDraft()
      .filter(link => link.is_active)
      .map(link => ({ link, url: normalizeSocialUrl(link.network, link.url) || '' }))
      .filter(item => !!item.url)
      .map(({ link, url }, index) => {
        const net = this.socialNetwork(link.network);
        return { id: index + 1, network: net.key, label: link.label.trim() || net.label, url, icon: net.icon, color: net.color, on_color: net.on_color };
      });
  }

  private broadcastDraftToPreview(payload: MiSitioDraftPayload): void {
    const win = this.previewIframeRef?.nativeElement?.contentWindow;
    if (!win) return;
    try {
      win.postMessage({ type: DRAFT_PREVIEW_MESSAGE_TYPE, payload }, this.clientAppBaseUrl);
    } catch {
      // Vista previa cross-origin no disponible en este momento: no es
      // crítico, el próximo cambio (o el próximo "load" del iframe) lo
      // vuelve a intentar.
    }
  }

  // --- Borrador / publicado ---
  async guardarBorrador(showSuccessMessage: boolean): Promise<boolean> {
    const businessId = this.currentBusiness()?.id;
    if (!businessId || !this.canCustomizeSite() || this.isSavingDraft()) return false;

    // Cambiar la URL pública no es un ajuste cosmético más: rompe cualquier
    // enlace ya compartido. Por eso, a diferencia del resto de "Mi sitio",
    // no se guarda en silencio junto con los demás campos -- se confirma
    // aparte. El backend igual la vuelve a validar (formato, reservada,
    // única en el sistema) y solo la acepta en plan Premium/exento.
    const currentSlug = this.mySite()?.slug || '';
    const newSlug = this.slugDraft().trim();
    if (newSlug && newSlug !== currentSlug) {
      const confirmed = await this.dialogs.confirm({
        title: 'Vas a cambiar la URL de tu sitio',
        message: `Tu enlace público pasará de "${this.clientAppBaseUrl}/${currentSlug}" a "${this.clientAppBaseUrl}/${newSlug}". Cualquier enlace ya compartido con la URL anterior dejará de funcionar.`,
        tone: 'warning',
        confirmText: 'Cambiar de todas formas',
        cancelText: 'Cancelar',
      });
      if (!confirmed) {
        this.slugDraft.set(currentSlug);
        return false;
      }
    }

    // Redes sociales: mismas reglas que el backend; si alguna falla se lleva
    // al dueño a la pestaña y a la fila exacta en vez de solo avisar.
    const social = validateSocialLinks(this.socialLinksDraft(), key => this.socialNetwork(key));
    if (!social.ok) {
      const index = Number(Object.keys(social.errors)[0]);
      this.activeSection.set('redes');
      this.socialLinksError.set({ index, message: social.errors[index] });
      this.notifications.error(social.errors[index]);
      return false;
    }

    this.isSavingDraft.set(true);
    try {
      let site = await firstValueFrom(this.businessConfigService.actualizarPersonalizacionSitio(businessId, {
        // Solo se envía si de verdad cambió: el backend trata la sola
        // presencia de "slug" en el body como una solicitud de cambio de URL
        // y la rechaza fuera del plan Premium -- enviarlo siempre, aunque
        // fuera idéntico al actual, bloqueaba "Guardar borrador" por completo
        // para cualquier plan que no pueda personalizar la URL.
        ...(newSlug !== currentSlug ? { slug: newSlug } : {}),
        heroTitle: this.heroTitleDraft().trim(),
        heroSubtitle: this.heroSubtitleDraft().trim(),
        ctaText: this.ctaTextDraft().trim(),
        primaryColor: this.primaryColorDraft(),
        secondaryColor: this.secondaryColorDraft(),
        backgroundColor: this.backgroundColorDraft(),
        navbarColor: this.navbarColorDraft(),
        highlightTextColor: this.highlightTextColorDraft(),
        shortDescription: this.shortDescriptionDraft().trim(),
        aboutText: this.aboutTextDraft().trim(),
        address: this.addressDraft().trim(),
        phone: this.phoneDraft().trim(),
        socialLinks: social.links.map(({ network, label, url, is_active }) => ({ network, label, url, is_active })),
        visibleSections: this.visibleSectionsDraft(),
        shareTitle: this.shareTitleDraft().trim(),
        shareDescription: this.shareDescriptionDraft().trim(),
        accessWelcomeTitle: this.accessWelcomeTitleDraft().trim(),
        accessWelcomeMessage: this.accessWelcomeMessageDraft().trim(),
      }));
      if (this.selectedBanner()) {
        site = await firstValueFrom(this.businessConfigService.actualizarBannerSitio(businessId, this.selectedBanner()!));
        this.cancelarBanner();
      }
      if (this.selectedShareImage()) {
        site = await firstValueFrom(this.businessConfigService.actualizarImagenCompartirSitio(businessId, this.selectedShareImage()!));
        this.cancelarShareImage();
      }
      this.applySiteToDrafts(site);
      if (showSuccessMessage) this.notifications.success('Borrador guardado.');
      return true;
    } catch (error: any) {
      this.notifications.error(friendlyError(error, 'No fue posible guardar el borrador.'));
      if (error?.error?.field === 'socialLinks') {
        this.activeSection.set('redes');
        const index = Number(error.error.index);
        this.socialLinksError.set({ index: Number.isInteger(index) ? index : null, message: friendlyError(error, 'Revisa el enlace de esta red.') });
      }
      return false;
    } finally {
      this.isSavingDraft.set(false);
    }
  }

  async publicarCambios(): Promise<void> {
    const businessId = this.currentBusiness()?.id;
    if (!businessId || !this.canCustomizeSite() || this.isPublishing()) return;
    this.isPublishing.set(true);
    try {
      const saved = await this.guardarBorrador(false);
      if (!saved) return;
      const site = await firstValueFrom(this.businessConfigService.publicarSitio(businessId));
      this.applySiteToDrafts(site);
      this.notifications.success('Tu sitio fue publicado.');
    } catch (error: any) {
      this.notifications.error(friendlyError(error, 'No fue posible publicar tu sitio.'));
    } finally {
      this.isPublishing.set(false);
    }
  }
}
